import type { RemoteAnswerKey, RemoteQuestionMode, RemoteQuestionType } from '../types/presentationRemote';

export const REMOTE_CHOICE_MIN = 2;
export const REMOTE_CHOICE_MAX = 6;
export const REMOTE_CHOICE_MAXLEN = 80;

export const QUESTION_TYPE_LABEL: Record<RemoteQuestionType, string> = {
  short: '단답',
  ox: 'OX',
  choice: '선택형',
  essay: '서술형',
};

/** 질문 유형 표시 순서 (리모컨·질문 페이지 공통): 단답 → OX → 선택형 → 서술형 */
export const REMOTE_QUESTION_TYPES: RemoteQuestionType[] = ['short', 'ox', 'choice', 'essay'];

/** 서술형은 정답 채점 없음 */
export const isGradableQuestionType = (t: RemoteQuestionType | undefined | null) => t !== 'essay';

const CIRCLED = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨'];
export const circledNum = (i: number) => CIRCLED[i] ?? `${i + 1}.`;

/** 누락·알 수 없는 값은 단답형(이전 버전 호환) */
export function normalizeQuestionType(v: unknown): RemoteQuestionType {
  return v === 'ox' || v === 'choice' || v === 'essay' ? v : 'short';
}

export function normalizeChoices(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v
    .map((c) => String(c ?? '').trim().slice(0, REMOTE_CHOICE_MAXLEN))
    .filter(Boolean)
    .slice(0, REMOTE_CHOICE_MAX);
}

/** 선택형 응답 표시 문자열 예: '③ 광합성' */
export const choiceAnswerText = (i: number, choice: string) => `${circledNum(i)} ${choice}`;

/** 여러 보기 응답 구분자 */
export const CHOICE_MULTI_SEP = ' / ';

/** 보기 번호 목록 정리: 정수·범위 안·중복 제거·오름차순 */
export function normalizeChoiceIndexes(v: unknown, choiceCount: number): number[] {
  const arr = Array.isArray(v) ? v : [];
  return Array.from(
    new Set(arr.map((x) => Number(x)).filter((i) => Number.isInteger(i) && i >= 0 && i < choiceCount))
  ).sort((a, b) => a - b);
}

/** 선택형 응답 문자열(여러 개면 번호순으로 ' / ' 연결). 하나면 choiceAnswerText와 같음(이전 버전 호환) */
export function choiceMultiAnswerText(indexes: number[], choices: string[]): string {
  return normalizeChoiceIndexes(indexes, choices.length)
    .map((i) => choiceAnswerText(i, choices[i]))
    .join(CHOICE_MULTI_SEP);
}

/** 정답 키의 선택형 정답 번호 목록(복수 정답 우선, 없으면 단일 choiceIndex) */
export function answerKeyChoiceIndexes(key: RemoteAnswerKey | null | undefined, choiceCount: number): number[] {
  if (!key) return [];
  const multi = normalizeChoiceIndexes(key.choiceIndexes, choiceCount);
  if (multi.length) return multi;
  return normalizeChoiceIndexes(key.choiceIndex != null ? [key.choiceIndex] : [], choiceCount);
}

/** 응답 문자열에서 고른 보기 번호들 복원(표시용) */
export function choiceAnswerPickedIndexes(content: string | null | undefined, choices: string[]): number[] {
  const c = (content ?? '').trim();
  if (!c) return [];
  const single = choices.findIndex((x, i) => choiceAnswerText(i, x) === c);
  if (single >= 0) return [single];
  const parts = c.split(CHOICE_MULTI_SEP).map((p) => p.trim());
  return choices.map((x, i) => (parts.includes(choiceAnswerText(i, x)) ? i : -1)).filter((i) => i >= 0);
}

/** 읽어주기 문장 */
export function questionSpeechText(text: string, type: RemoteQuestionType, choices: string[] = []): string {
  if (type === 'ox') return `${text}. O 또는 X로 답해 주세요.`;
  if (type === 'choice' && choices.length) return `${text}. ${choices.map((c, i) => `${i + 1}번, ${c}.`).join(' ')}`;
  return text;
}

export function normalizeQuestionMode(v: unknown): RemoteQuestionMode {
  return v === 'answer' ? 'answer' : 'submit';
}

/** 단답 비교: 앞뒤 공백 제거, 전각/반각 통일, 대소문자·공백 무시 */
export const normalizeShortAnswer = (v: string) => v.normalize('NFKC').toLowerCase().replace(/\s+/g, '');

/** 받은 정답 키를 유형에 맞게 정리 — 쓸 수 없으면 null(=제출모드로 처리) */
export function normalizeAnswerKey(v: unknown, type: RemoteQuestionType, choiceCount: number): RemoteAnswerKey | null {
  if (!v || typeof v !== 'object') return null;
  const k = v as RemoteAnswerKey;
  if (type === 'essay') return null;
  if (type === 'ox') return k.ox === 'O' || k.ox === 'X' ? { ox: k.ox } : null;
  if (type === 'choice') {
    const idx = answerKeyChoiceIndexes(k, choiceCount);
    if (!idx.length) return null;
    return idx.length > 1 ? { choiceIndex: idx[0], choiceIndexes: idx } : { choiceIndex: idx[0] };
  }
  const texts = Array.isArray(k.texts)
    ? k.texts.map((t) => String(t ?? '').trim().slice(0, 100)).filter(Boolean).slice(0, 5)
    : [];
  return texts.length ? { texts } : null;
}

/** 단답 정답 입력: 쉼표(,)로만 여러 정답 구분 */
export const splitShortKeys = (raw: string) =>
  raw
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
    .slice(0, 5);

/** 정답 표시 문자열 */
export function answerKeyText(type: RemoteQuestionType, key: RemoteAnswerKey | null | undefined, choices: string[] = []): string {
  if (!key || type === 'essay') return '';
  if (type === 'ox') return key.ox ?? '';
  if (type === 'choice') return choiceMultiAnswerText(answerKeyChoiceIndexes(key, choices.length), choices);
  return (key.texts ?? []).join(', ');
}

/** 채점: 정답 키가 없으면 null */
export function judgeAnswer(
  type: RemoteQuestionType,
  key: RemoteAnswerKey | null | undefined,
  content: string,
  choices: string[] = []
): boolean | null {
  if (!key || type === 'essay') return null;
  const c = content.trim();
  if (type === 'ox') return key.ox ? c === key.ox : null;
  if (type === 'choice') {
    // 복수 정답: 고른 보기 집합이 정답 집합과 정확히 같아야 정답
    const idx = answerKeyChoiceIndexes(key, choices.length);
    return idx.length ? c === choiceMultiAnswerText(idx, choices) : null;
  }
  const n = normalizeShortAnswer(c);
  return (key.texts ?? []).some((t) => normalizeShortAnswer(t) === n);
}
